import { encode as base64_encode } from 'base-64';
import { AuthHTTPService } from './AuthHttpService';
import { createLogger } from '../utils/logger';

const logger = createLogger('AuthService');

export interface UserDetails {
  id: number;
  name: string;
  role: string;
  mustChangePassword: boolean;
}

export type ChangeAccessCodeError = 'INVALID_CURRENT_PIN' | 'INVALID_NEW_PIN' | 'UNKNOWN';

export interface ChangeAccessCodeResult {
  success: boolean;
  error?: ChangeAccessCodeError;
}

export interface IAuthService {
  login(pin: string): Promise<boolean>;
  refreshLogin(): Promise<boolean>;
  logout(): Promise<boolean>;
  readUser(): Promise<UserDetails | undefined>;
  changeAccessCode(currentPin: string, newPin: string): Promise<ChangeAccessCodeResult>;
}

export class AuthService implements IAuthService {
  private authHttpService: AuthHTTPService;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(authHttpService: AuthHTTPService) {
    this.authHttpService = authHttpService;
  }

  public async login(pin: string): Promise<boolean> {
    const idt = base64_encode(pin);
    logger.debug('login()');

    try {
      const response = await this.authHttpService.getClient().post('/auth/login', {
        identity: idt,
      });

      return response?.status === 200;
    } catch (error) {
      return false;
    }
  }

  public async refreshLogin(): Promise<boolean> {
    // De-duplicate concurrent refresh calls: if a refresh is already in-flight,
    // wait for that pending promise instead of firing a duplicate POST request.
    // This prevents the "thundering herd" of concurrent 403s from triggering
    // false-positive token-theft detection (reuse on the backend).
    if (this.refreshPromise !== null) {
      logger.debug('refreshLogin() already in-flight, reusing pending promise');
      return this.refreshPromise;
    }

    // Create the refresh promise and store it so concurrent callers can share it
    this.refreshPromise = (async () => {
      try {
        const response = await this.authHttpService.getClient().post('/auth/refresh_token', {});
        logger.debug('refreshLogin() succeeded');
        return response?.status === 200;
      } catch (error) {
        logger.error('refreshLogin() error:', error);
        return false;
      } finally {
        // Clear the pending promise so future genuine refreshes work normally
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  public async logout(): Promise<boolean> {
    try {
      const response = await this.authHttpService.getClient().post('/auth/logout', {});
      logger.debug('logout() - request sent to backend');
      return response?.status === 200;
    } catch (error) {
      logger.error('logout() error:', error);
      return false;
    }
  }

  public async readUser(): Promise<UserDetails | undefined> {
    try {
      const response = await this.authHttpService.getClient().get('/users/me');

      logger.debug('user data:', JSON.stringify(response?.data));
      return response?.data;
    } catch (error) {
      logger.error('readUser() error:', error);
      return undefined;
    }
  }

  /**
   * Change the current user's access code (PIN). Requires the CURRENT PIN
   * to be re-submitted alongside the new one; the server re-validates it
   * even though the caller already has a valid session (defense-in-depth).
   */
  public async changeAccessCode(currentPin: string, newPin: string): Promise<ChangeAccessCodeResult> {
    logger.debug('changeAccessCode()');
    try {
      const response = await this.authHttpService.getClient().post('/users/me/password', {
        currentPin: base64_encode(currentPin),
        newPin: base64_encode(newPin),
      });
      return { success: response?.status === 204 };
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401) {
        return { success: false, error: 'INVALID_CURRENT_PIN' };
      }
      if (status === 400) {
        return { success: false, error: 'INVALID_NEW_PIN' };
      }
      logger.error('changeAccessCode() error:', error);
      return { success: false, error: 'UNKNOWN' };
    }
  }
}
